import { attachmentApi } from '@/store/services/attachment.service'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  createSearchParams,
  generatePath,
  useLoaderData,
  useSearchParams
} from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from '@material-tailwind/react'
import { Pill } from '@/shared/components/pill'
import { store } from '@/store'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'
import { useCreateZipAttachmentMutation } from '@/store/services/zip-attachment.service'
import { ZipDownloadRangeDialog } from '@/shared/components/zip-download-range-dialog'

export const ContractAttachments = () => {
  const { contract: currentContract } = useLoaderData()
  const [searchParams] = useSearchParams()
  const [isNavigatingToUpload, setIsNavigatingToUpload] = useState(false)
  const [showUploadLoadingMessage, setShowUploadLoadingMessage] = useState(false)
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })

  const { t } = useTranslation()
  const modalSearchParams = useMemo(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('attachment_tab')
    nextParams.delete('attachment_reload')
    return nextParams
  }, [searchParams])
  const currentUrlParams = modalSearchParams.toString()
    ? `?${modalSearchParams.toString()}`
    : ''
  const defaultSearchParams = useMemo(() => {
    const reloadToken = searchParams.get('attachment_reload')

    return {
      attachable_type: 'Contractor::Contract',
      attachable_id: currentContract.id,
      ...(reloadToken ? { attachment_reload: reloadToken } : {})
    }
  }, [currentContract.id, searchParams])

  const pillOptions = useMemo(() => {
    return [
      { label: t('contract.id'), value: currentContract.id },
      { label: t('company.name'), value: currentContract.company.name },
      { label: t('documentNumber.one'), value: currentContract.company.documentNumber }
    ]
  }, [currentContract])

  const getMore = async (searchParams) => {
    try {
      const {
        attachments = [], meta = {}
      } = await store.dispatch(
        attachmentApi.endpoints.getAttachments.initiate(
          `?${searchParams.toString()}`,
          { forceRefetch: true }
        )
      ).unwrap()

      const currentPage = Number(searchParams.get('page'))
      const hasMore = currentPage < meta?.pagination?.total_pages

      return {
        attachments,
        hasMore
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    hasAccess('contract#attachments')
  }, [])

  useEffect(() => {
    if (!isNavigatingToUpload) {
      setShowUploadLoadingMessage(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowUploadLoadingMessage(true)
    }, 500)

    return () => window.clearTimeout(timeoutId)
  }, [isNavigatingToUpload])

  // ---- ZIP: state and logic ----
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false)
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false)
  const [existingExportInfo, setExistingExportInfo] = useState(null)
  const [pendingZipRequest, setPendingZipRequest] = useState(null)

  const [createZipAttachment, { isLoading: isCreatingZip }] =
    useCreateZipAttachmentMutation()

  const requestZip = async ({ fromDate, toDate }, { force = false } = {}) => {
    try {
      const response = await createZipAttachment({
        attachable_type: 'Contractor::Contract',
        attachable_id: currentContract.id,
        from_date: fromDate,
        to_date: toDate,
        ...(force ? { force: true } : {})
      }).unwrap()

      if (response?.conflict && response?.existing_export && !force) {
        setExistingExportInfo(response.existing_export)
        setPendingZipRequest({ fromDate, toDate })
        setIsConflictDialogOpen(true)
        return
      }

      // Success: close all related dialogs
      setIsZipDialogOpen(false)
      setIsConflictDialogOpen(false)
      setExistingExportInfo(null)
      setPendingZipRequest(null)

    } catch (error) {
      //console.error('Error requesting ZIP', error)

      // Keep other errors with the existing behavior
    }
  }

  const handleZipConfirm = ({ fromDate, toDate }) => {
    // First request without "force"
    requestZip({ fromDate, toDate }, { force: false })
  }

  const handleConflictCancel = () => {
    setIsConflictDialogOpen(false)
    setExistingExportInfo(null)
    setPendingZipRequest(null)
  }

  const handleConflictConfirm = () => {
    if (!pendingZipRequest) return
    requestZip(pendingZipRequest, { force: true })
  }

  const blockWhileNavigating = (event) => {
    if (!isNavigatingToUpload) return

    event.preventDefault()
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>

      <DialogBody className='relative overflow-hidden' style={{ height: 'inherit' }}>
        {isNavigatingToUpload && (
          <div className='absolute inset-0 z-10 bg-white/55 pointer-events-auto'>
            {showUploadLoadingMessage && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='rounded-lg bg-white px-4 py-3 shadow-md'>Cargando...</div>
              </div>
            )}
          </div>
        )}
        <div className='flex gap-3 mb-5'>
          {!!pillOptions.length &&
            pillOptions.map(({ value, label }) => (
              <Pill key={value} label={label} value={value} />
            ))}
        </div>

        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
          initialTab={searchParams.get('attachment_tab')}
          onUploadNavigateStart={() => setIsNavigatingToUpload(true)}
          buildUploadPath={(attachment, currentTab) =>
            generatePath(
              '/contracts/:id/attachments/:attachmentId/upload_attachment',
              {
                id: currentContract.id,
                attachmentId: attachment.id
              }
            ) + `?${createSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              attachment_tab: currentTab
            }).toString()}`
          }
        />
      </DialogBody>

      <DialogFooter className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        {/* Button to open the period dialog */}
        <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
          <HasAccess permissions="attachment#download_batch">
            <Button
              variant='outlined'
              onClick={() => setIsZipDialogOpen(true)}
              disabled={isCreatingZip || isNavigatingToUpload}
            >
              Descargar documentos por período
            </Button>
          </HasAccess>
        </div>

        {/* Existing buttons */}
        <div className='flex gap-3'>
          <Link to={`/contracts${currentUrlParams}`} onClick={blockWhileNavigating}>
            <Button
              variant='outlined'
              color='white'
              disabled={isNavigatingToUpload}
            >
              Volver
            </Button>
          </Link>

          <Link
            to={generatePath('/contracts/:id/attachments/upload', { id: currentContract.id }) + currentUrlParams}
            onClick={blockWhileNavigating}
          >
            <Button disabled={isNavigatingToUpload}>
              Añadir documento
            </Button>
          </Link>
        </div>
      </DialogFooter>

      {/* Range selection dialog + conflict handling */}
      <ZipDownloadRangeDialog
        open={isZipDialogOpen}
        onClose={() => setIsZipDialogOpen(false)}
        onConfirm={handleZipConfirm}
        isLoading={isCreatingZip}
        title='Descargar documentos por período'
        conflict={{
          open: isConflictDialogOpen,
          existingExport: existingExportInfo,
          isLoading: isCreatingZip,
          onCancel: handleConflictCancel,
          onConfirm: handleConflictConfirm
        }}
      />
    </Dialog>
  )
}
